import { Col, message, Row, Typography } from 'antd'
import { ITransaction } from 'api'
import { CopyOutlined } from '@ant-design/icons'
import Container from 'components/Container'
import { useLocalization } from 'localization'
import { FC, useMemo } from 'react'
import { useTheme } from 'styled-components'
import { shortenTransactionId } from './helpers'
import Styled from './styled'
import { useUserCurrency } from 'modules/common/hooks'
import { useScreenMatch } from 'theme/hooks'
import CurrencyValueSimplified from 'components/CurrencyValue/CurrencyValueSimplified'

interface ITransactionDetails extends ITransaction {
	short?: boolean
}

const TransactionDetails: FC<ITransactionDetails> = ({
	blockchainId,
	fee,
	out,
	in: _in,
	forkBaseCurrency,
	group,
	note,
	short,
	additionalCosts,
}) => {
	const { t, f } = useLocalization()
	const theme = useTheme()
	const currency = useUserCurrency()
	const match = useScreenMatch()

	const copyBlockchainId = async () => {
		if (blockchainId) {
			await navigator.clipboard.writeText(blockchainId)
		}
		message.success(t('transactions.details.copySuccess'))
	}

	const feeValuation = useMemo(
		() => fee?.valuation || (fee?.amount && fee.perCoin ? fee.amount * fee.perCoin : undefined),
		[fee]
	)

	return (
		<Styled.Wrapper space="md" $short={short} spaceDirection="vertical">
			<Styled.LayoutRow gutter={[theme.margins.xl, theme.margins.md]}>
				<Col xs={24} sm={12}>
					<Container>
						{match.gte('lg') && (
							<Row gutter={[theme.margins.sm, 0]}>
								{!short && <Col span={24}></Col>}
								<Col flex="230px">
									<Typography.Text strong>{t('terms.amount')}</Typography.Text>
								</Col>
								<Col flex="120px">
									<Typography.Text strong>{t('terms.perCoin')}</Typography.Text>
								</Col>
								<Col flex="8">
									<Container display="flex">
										<Typography.Text strong>{t('terms.wallet')}</Typography.Text>
									</Container>
								</Col>
							</Row>
						)}
						{(out?.amount || out?.amount === 0) && (
							<Row gutter={[theme.margins.sm, 0]}>
								{!short && (
									<Col span={24}>
										<Typography.Text strong>{t('terms.outgoing')}</Typography.Text>
									</Col>
								)}
								<Col flex={match.gte('md') ? '230px' : '150px'}>
									<Container display="flex" alignItems="center" space="xxs">
										<CurrencyValueSimplified amount={out.amount} currency={out.currency} />
										{out?.valuation && (
											<Styled.Valuation type="secondary">({f(out.valuation, { currency })})</Styled.Valuation>
										)}
									</Container>
								</Col>
								<Col flex="120px">
									<CurrencyValueSimplified amount={out.perCoin} currency={currency} />
								</Col>
								<Col flex={match.gte('md') ? '8' : '6'}>
									<Container display="inline-grid" flexWrap="nowrap">
										{out?.wallet?.name ? (
											<Typography.Text ellipsis title={`${t('terms.wallet')}: ${out.wallet.name}`}>
												{out.wallet.name}
											</Typography.Text>
										) : (
											out?.wallet?.address && (
												<Typography.Text ellipsis title={`${t('terms.wallet')}: ${out?.wallet?.address}`}>
													{out?.wallet?.address}
												</Typography.Text>
											)
										)}
									</Container>
								</Col>
							</Row>
						)}
						{(_in?.amount || _in?.amount === 0) && (
							<Row gutter={[theme.margins.sm, 0]}>
								{!short && (
									<Col span={24}>
										<Typography.Text strong>{t('terms.incoming')}</Typography.Text>
									</Col>
								)}
								<Col flex={match.gte('md') ? '230px' : '150px'}>
									<Container display="flex" alignItems="center" space="xxs">
										<CurrencyValueSimplified amount={_in?.amount} currency={_in.currency} />
										{_in?.valuation && (
											<Styled.Valuation type="secondary">({f(_in.valuation, { currency })})</Styled.Valuation>
										)}
									</Container>
								</Col>
								<Col flex="120px">
									<CurrencyValueSimplified amount={_in.perCoin} currency={currency} />
								</Col>
								<Col flex={match.gte('md') ? '8' : '6'}>
									<Container display="inline-grid">
										{_in?.wallet?.name ? (
											<Typography.Text ellipsis title={`${t('terms.wallet')}: ${_in.wallet.name}`}>
												{_in.wallet.name}
											</Typography.Text>
										) : (
											_in?.wallet?.address && (
												<Typography.Text ellipsis title={`${t('terms.wallet')}: ${_in?.wallet?.address}`}>
													{_in?.wallet?.address}
												</Typography.Text>
											)
										)}
									</Container>
								</Col>
							</Row>
						)}
						{fee?.currency && (
							<Row gutter={[theme.margins.sm, 0]}>
								{!short && (
									<Col span={24}>
										<Typography.Text strong>{t('terms.fees')}</Typography.Text>
									</Col>
								)}
								<Col flex={match.gte('md') ? '230px' : '150px'}>
									<Container display="flex" alignItems="center" space="xxs">
										<CurrencyValueSimplified amount={fee.amount} currency={fee.currency} />
										{feeValuation && (
											<Styled.Valuation type="secondary">({f(feeValuation, { currency })})</Styled.Valuation>
										)}
									</Container>
								</Col>
								<Col flex="120px">
									<CurrencyValueSimplified amount={fee.perCoin} currency={currency} />
								</Col>
								<Col flex={match.gte('md') ? '8' : '6'}></Col>
							</Row>
						)}
					</Container>
				</Col>
				<Styled.RightCol xs={24} sm={12}>
					<Container space="xxs" spaceDirection="vertical" margin={{ top: 'xs' }}>
						{blockchainId && (
							<Container>
								<Typography.Text strong>{t('transactions.transactionModal.blockchainTransactionId')}: </Typography.Text>
								<Typography.Text>
									{shortenTransactionId(blockchainId)}{' '}
									<Styled.CopyIconContainer>
										<CopyOutlined onClick={copyBlockchainId} />
									</Styled.CopyIconContainer>
								</Typography.Text>
							</Container>
						)}
						{forkBaseCurrency && (
							<Container>
								<Typography.Text strong>{t('transactions.transactionModal.forkedFromCurrency')}: </Typography.Text>
								<Typography.Text>{forkBaseCurrency}</Typography.Text>
							</Container>
						)}
						{group && (
							<Container>
								<Typography.Text strong>{t('transactions.transactionModal.group')}: </Typography.Text>
								<Typography.Text>{group}</Typography.Text>
							</Container>
						)}
						{note && (
							<Container>
								<Typography.Text strong>{t('transactions.transactionModal.note')}: </Typography.Text>
								<Typography.Text>{note}</Typography.Text>
							</Container>
						)}
						{additionalCosts && (
							<Container>
								<Typography.Text strong>{t('transactions.transactionModal.additionalCosts')}: </Typography.Text>
								<Typography.Text>{f(additionalCosts, { currency })}</Typography.Text>
							</Container>
						)}
					</Container>
				</Styled.RightCol>
			</Styled.LayoutRow>
		</Styled.Wrapper>
	)
}

export default TransactionDetails
